import { createCommands } from "@tdp/common/commandHandler";
import { ApiFetchStatus } from "@tdp/common/constants";
import { notificationSlice, NotificationType } from "@tdp/common/notification";
import { ILogger } from "@tdp/common/telemetry";

import { adaptiveCardSlice } from "./slice";
import { IAdaptiveCardItem } from "../abstractions/IAdaptiveCards";
import { IAdaptiveCardsReader } from "../abstractions/IAdaptiveCardsReader";
import { IAdaptiveCardsWriter } from "../abstractions/IAdaptiveCardsWriter";

export interface IAdaptiveCardCommandHandler {
  logger: ILogger;
  adaptiveCardsReader: IAdaptiveCardsReader;
  adaptiveCardsWriter: IAdaptiveCardsWriter
}

export const createAdaptiveCardCommandHandler = ({ logger, adaptiveCardsReader, adaptiveCardsWriter }: IAdaptiveCardCommandHandler) => createCommands({
  name: "adaptiveCardCommands",
  handlers: {

    async *getCards() {
      const response = await adaptiveCardsReader.readAdaptiveCards()
      if (response.type === ApiFetchStatus.SUCCESS && response.data) {
        yield adaptiveCardSlice.actions.cardsLoaded(response.data);
      }
      if (response.type === ApiFetchStatus.ERROR) {
        yield notificationSlice.actions.showNotification({
          message: `Unable to get adaptive cards`,
          show: true,
          autoDismiss: true,
          type: NotificationType.Error,
        });
      }
      return;
    },

    async *resetNewCard() {
      yield adaptiveCardSlice.actions.resetNewCard();
      return;
    },

    async *createCard(payload: IAdaptiveCardItem) {
      const response = await adaptiveCardsWriter.createAdaptiveCard(payload);
      if (response.type === ApiFetchStatus.SUCCESS && response.data) {
        yield adaptiveCardSlice.actions.newCreatedSuccess(response.data);
        yield notificationSlice.actions.showNotification({
          message: `Adaptive card created`,
          show: true,
          autoDismiss: true,
          type: NotificationType.Success,
        });
        logger.logUserBi({
          telemetryMarker: 'api-action-adaptive-card-create-success',
          message: `User created adaptive card with id: ${response.data.id}`,
        });
      } else {
        logger.logUserBi({
          telemetryMarker: 'api-action-adaptive-card-create-failure',
          message: `User failed to create adaptive card`,
        });
        yield notificationSlice.actions.showNotification({
          message: `Unable to create adaptive card`,
          show: true,
          autoDismiss: true,
          type: NotificationType.Error,
        });
      }
      return;
    },

    async *updateCard(payload: IAdaptiveCardItem) {
      const response = await adaptiveCardsWriter.updateAdaptiveCard(payload);
      if (response.type === ApiFetchStatus.SUCCESS && response.data) {
        yield adaptiveCardSlice.actions.cardUpdatedSuccess(response.data);
        yield notificationSlice.actions.showNotification({
          message: `Adaptive card updated`,
          show: true,
          autoDismiss: true,
          type: NotificationType.Success,
        });
      } else {
        yield notificationSlice.actions.showNotification({
          message: `Unable to update adaptive card`,
          show: true,
          autoDismiss: true,
          type: NotificationType.Error,
        });
      }
      return;
    },

    async *deleteCard(id: string) {
      const response = await adaptiveCardsReader.deleteAdaptiveCard(id);
      if (response.type === ApiFetchStatus.SUCCESS) {
        yield adaptiveCardSlice.actions.listItemDeleted(id);
        yield notificationSlice.actions.showNotification({
          message: `Adaptive card deleted`,
          show: true,
          autoDismiss: true,
          type: NotificationType.Success,
        });
      } else {
        yield notificationSlice.actions.showNotification({
          message: `Unable to delete adaptive card`,
          show: true,
          autoDismiss: true,
          type: NotificationType.Error,
        });
      }
      return;
    },

    async *sendMe(payload: string) {
      const response = await adaptiveCardsWriter.sendMe(payload);
      if (response.type === ApiFetchStatus.SUCCESS) {
        yield notificationSlice.actions.showNotification({
          message: `Adaptive card sent successfully`,
          show: true,
          autoDismiss: true,
          type: NotificationType.Success,
        });
      } else {
        yield notificationSlice.actions.showNotification({
          message: `Unable to send adaptive card`,
          show: true,
          autoDismiss: true,
          type: NotificationType.Error,
        });
      }
      return;
    }

  }
});
